/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AbstractObject } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { TenantService } from '@src/app/shared/tenant.service';

/**
 * Agent managed data - rows the OMC owns but the agency works across all of its OMCs at once
 * (ServiceRequest, Action). An agent standing in the agency sees them for every OMC they act for,
 * so a write from there would otherwise post to the agency's own tenant, where ForceTeam has
 * nothing to switch: the OMC's `agents` grant is never consulted and the agency's own role decides
 * the privileges. Addressing the write to the record's tenant is what puts those checks back.
 *
 * Only needed for models editable from inside an agency. Bank accounts and fixed assets are agent
 * managed too but are only ever edited from within the OMC, where the page tenant is already right.
 *
 * teamId must therefore be in the body of every write. It is on the forms already (the request
 * page's teamField, the action dialog's teamId holder), and a write without it is a bug rather
 * than a default to fall back on - guessing the current tenant is how the hole opened.
 */
export abstract class AbstractAgentManagedHttpService extends AbstractHttpService {

    protected override writeUrl(object: AbstractObject): string {
        const teamId = object?.teamId;
        if (!teamId) {
            throw new Error(`${this.typeString} cannot be saved without a teamId - see AbstractAgentManagedHttpService`);
        }
        if (teamId === this.tenantSvc.getCurrentTenantId()) {
            return this.baseUrl;
        }
        return TenantService.bareAjaxPath + teamId + '/'
            + this.baseUrl.substring(this.tenantSvc.ajaxPath.length);
    }
}
